<script lang="ts">
  /**
   * L'écran Sac : ce que le personnage porte, ce qu'il traîne, ce qu'il vend.
   *
   * # Trois états, et non deux
   *
   * **Équipé** : sur soi, prêt à servir — seule une armure équipée protège.
   * **Dans le sac** : ça pèse autant, ça ne sert pas tout de suite.
   * **Laissé** : resté à la maison. Ça reste à vous, ça ne pèse plus rien.
   *
   * Le troisième manquait, et son absence coûtait cher : « rangé » mélangeait
   * ce qu'on a sur le dos et ce qu'on n'a pas emporté, si bien qu'on
   * trimballait le poids d'une armure restée au presbytère. Le livre ne nomme
   * pas ces états mais les suppose partout.
   *
   * L'autre chose que la fiche taisait : **revendre ne rend que la moitié** du
   * prix d'achat (Codex V p.150).
   *
   * La réparation garde sa place ici : c'est un Test d'Artisanat Complexe qui
   * consomme un Matériel **à chaque tentative, réussie ou non** (Codex XII
   * p.248). Le dé se jette dans le tiroir ; le joueur rapporte le verdict.
   */
  import Renforcement from './sac/Renforcement.svelte'
  import Chiffres from '../../composants/Chiffres.svelte'
  import {
    articles,
    equipementsAbimes,
    materielDeReparation,
    protections,
    sac as lireLeSac,
    type Fiche,
    type LigneDeSac,
    type Rangement,
    type CibleEcrite,
    type PersonnageEnregistre,
  } from '../../rules'

  let {
    vue,
    personnage,
    reparer,
    ramasser,
    inscrire,
    renforcer,
    ameliorer,
    monter,
    attribuer,
    bourse,
    seDefaire,
    rangerObjet,
    reprendre,
    chiffrerObjet,
    corrigerChiffreObjet,
    effacerChiffreObjet,
    renommerObjet,
    annoterObjet,
    ouvrirJet,
  }: {
    vue: Fiche
    personnage: PersonnageEnregistre
    /** Consomme un Matériel, et remet l'objet en état si le Test a réussi. */
    reparer: (objet: string, reussi: boolean) => void
    ramasser: (objet: string, payer: boolean) => void
    /** Inscrit un objet qu'aucun catalogue ne porte. */
    inscrire: (nom: string, encombrement: number, prix: number | null, note: string | null) => void
    /** Pose un Renforcement sur un exemplaire, ou l'en retire. */
    renforcer: (objet: string, source: string, sens: number) => void
    /** Tente une amélioration d'Artisanat : le Matériel part, réussie ou non. */
    ameliorer: (objet: string, propriete: string, reussi: boolean) => void
    /** Monte une Amélioration de Veredium sur un exemplaire, ou la démonte. */
    monter: (objet: string, amelioration: string) => void
    /** Attribue une Propriété sans rien consommer : un allié a fait le Test. */
    attribuer: (objet: string, propriete: string) => void
    /** Donne, ou retire, de l'argent. */
    bourse: (delta: number) => void
    seDefaire: (objet: string, vendre: boolean) => void
    /** Équipe l'objet, le range dans le sac, ou le laisse à la maison. */
    rangerObjet: (objet: string, ou: Rangement) => void
    /** Chiffre un objet inventé à la table : il ne pèse sur un jet que porté. */
    chiffrerObjet: (objet: string, cible: CibleEcrite, id: string, valeur: number) => void
    /** Change la valeur d'un chiffre déjà posé, sa cible inchangée. */
    corrigerChiffreObjet: (objet: string, position: number, valeur: number) => void
    effacerChiffreObjet: (objet: string, position: number) => void
    /** Reprend un objet déjà écrit à la main : un de plus sur la pile. */
    reprendre: (nom: string) => void
    /** Corrige le nom d'un objet écrit à la main. */
    renommerObjet: (objet: string, nom: string) => void
    /** Change sa note. Vidée, elle s'efface. */
    annoterObjet: (objet: string, note: string) => void
    /** Ouvre le tiroir sur une Compétence — ici, l'Artisanat du réparateur. */
    ouvrirJet: (competence: string) => void
  } = $props()

  const contenu = $derived(lireLeSac(personnage))
  const abimes = $derived(equipementsAbimes(personnage))
  const materiel = $derived(materielDeReparation(personnage))

  /** Le catalogue ne bouge pas : on le lit une fois. */
  const catalogue = articles()
  let recherche = $state('')
  const trouves = $derived(
    recherche.trim().length < 2
      ? []
      : catalogue
          .filter((a) => a.nom.toLowerCase().includes(recherche.trim().toLowerCase()))
          .slice(0, 12),
  )

  /* L'inventaire se lit par état plutôt qu'en une liste unique : la question
     d'avant une séance est « qu'est-ce que j'emporte ? », et une liste plate y
     répond mal. L'ordre est celui de la main — ce qu'on a sur soi d'abord. */
  const ETATS: { id: Rangement; titre: string; vide: string }[] = [
    { id: 'equipe', titre: 'Sur soi', vide: 'Rien sur le dos.' },
    { id: 'sac', titre: 'Dans le sac', vide: 'Le sac est vide.' },
    { id: 'laisse', titre: 'Laissé à la maison', vide: 'Vous avez tout emporté.' },
  ]
  const parEtat = $derived(
    ETATS.map((e) => ({ ...e, lignes: contenu.lignes.filter((l) => l.rangement === e.id) })),
  )
  /* Ce qu'on a déjà écrit à la main, nom par nom : ça se reprend d'un geste
     au lieu de se ressaisir. Clyde avait tapé deux fois le même lingot. */
  const dejaEcrits = $derived(
    [...new Set(contenu.lignes.filter((l) => l.genre === 'libre').map((l) => l.nom))].sort(),
  )

  /** Ce qu'on ne porte pas ne pèse pas — autant le dire plutôt que le sous-entendre. */
  const laisse = $derived(contenu.lignes.filter((l) => !l.emporte).length)

  /* La silhouette mise à plat. Le livre tire la Localisation touchée au 1D100
     (Codex VI p.163) : le Torse en prend trente sur cent, un bras quinze. Une
     bande découpée aux bornes de cette table le montre d'un coup d'œil, là où
     un dessin de corps donne au Torse la taille d'un torse. Le moteur ne
     retient qu'une pièce par zone — la meilleure après usure — si bien qu'un
     trou dans la couverture se lit comme un trou. */
  const zones = $derived(
    protections(personnage).map((z) => ({ ...z, part: z.max - z.min + 1 })),
  )
  const moyenne = $derived(zones.reduce((s, z) => s + (z.part / 100) * z.points, 0))
  const nues = $derived(zones.filter((z) => z.points === 0))
  const partNue = $derived(nues.reduce((s, z) => s + z.part, 0))
  const resumeDesZones = $derived(
    'Protection par Localisation : ' + zones.map((z) => `${z.nom} ${z.points}`).join(', ') + '.',
  )

  /* Le livre n'accorde que trois emplacements d'arme, et une seule arme
     active à la fois (Codex V). La fiche ne l'empêche pas — le meneur
     tranche — mais elle cesse de le taire. */
  const armesSurSoi = $derived(
    contenu.lignes.filter((l) => l.rangement === 'equipe' && l.genre === 'arme').length,
  )

  /* Le glisser-déposer double les trois boutons plutôt que de les remplacer :
     il va plus vite à la souris, et les boutons restent le chemin sûr — au
     clavier, au doigt, et pour qui lit la page autrement. */
  let glisse = $state<{ id: string; depuis: Rangement } | null>(null)

  function prendre(e: DragEvent, o: LigneDeSac) {
    glisse = { id: o.id, depuis: o.rangement }
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', o.id)
    }
  }
  /** Un état n'accepte que ce qui n'y est pas déjà. */
  function survoler(e: DragEvent, ou: Rangement) {
    if (glisse !== null && glisse.depuis !== ou) e.preventDefault()
  }
  function deposer(e: DragEvent, ou: Rangement) {
    e.preventDefault()
    if (glisse !== null && glisse.depuis !== ou) rangerObjet(glisse.id, ou)
    glisse = null
  }

  /** Un exemplaire de plus : repris par son nom s'il est écrit, ramassé sinon. */
  function unDePlus(o: LigneDeSac) {
    if (o.genre === 'libre') reprendre(o.nom)
    else ramasser(o.id, false)
  }

  const GENRES: Record<string, string> = {
    arme: 'Arme',
    armure: 'Armure',
    objet: 'Objet',
    libre: 'Écrit à la main',
  }

  /* Ce que le joueur invente. Le livre ne prétend pas tout lister, et une
     table invente : une lettre scellée, un souvenir, un butin du meneur. */
  let ouvert = $state(false)
  let nom = $state('')
  let poids = $state(0)
  let valeur = $state<number | null>(null)
  let note = $state('')

  function inscrireEtRefermer() {
    if (nom.trim().length === 0) return
    inscrire(nom, poids, valeur, note.trim() || null)
    nom = ''
    poids = 0
    valeur = null
    note = ''
    ouvert = false
  }
  const enBourse = $derived(contenu.argent ?? 0)
  /** Ce qu'on s'apprête à donner, ou à retirer. */
  let somme = $state(50)

</script>

<div class="entete">
  {#if contenu.argent !== null}
    <div class="cartouche">
      <div class="bandeau">Bourse</div>
      <div class="corps">
        <p class="argent data">{contenu.argent} £</p>
        <!-- Le livre chiffre les prix, pas le butin : c'est la table qui le
             dit, et la fiche qui s'en souvient. -->
        <div class="mouvement">
          <input
            class="saisie court"
            type="number"
            min="1"
            bind:value={somme}
            aria-label="Somme à donner ou retirer"
          />
          <button type="button" class="pilule" onclick={() => bourse(somme)}>Donner</button>
          <button
            type="button"
            class="pilule"
            disabled={enBourse === 0}
            onclick={() => bourse(-somme)}
          >
            Retirer
          </button>
        </div>
      </div>
    </div>
  {/if}

  <div class="cartouche">
    <div class="bandeau">Encombrement</div>
    <div class="corps">
      <p class="charge data" class:trop={contenu.expose_a_epuise}>
        {contenu.portee} / {contenu.maximum}
      </p>
      {#if contenu.expose_a_epuise}
        <p class="alerte">
          Surchargé : le livre expose alors au Statut Épuisé, qui coûte −10 à
          trois familles de Compétences.
        </p>
      {/if}
      {#if laisse > 0}
        <p class="detail">
          {laisse} objet{laisse > 1 ? 's' : ''} laissé{laisse > 1 ? 's' : ''} à la
          maison — ils ne comptent pas ici.
        </p>
      {/if}
    </div>
  </div>
</div>

<div class="cartouche">
  <div class="bandeau">Où les coups tombent</div>
  <div class="corps">
    <p class="regle">
      Le livre tire la Localisation touchée au <strong>1D100</strong> : chaque
      zone est ici large comme sa part des coups. Le chiffre est ce que votre
      armure y arrête — seule une pièce <em>sur soi</em> protège, et c'est la
      meilleure qui compte, jamais la somme.
    </p>
    <!-- Les cellules sont celles du système — `.loc`, son libellé, ses Points
         de protection, sa plage. Seule la mise en bande est d'ici : chacune
         large comme sa part du dé, au lieu de la grille en forme de corps. -->
    <div class="localisations" role="img" aria-label={resumeDesZones}>
      {#each zones as z (z.localisation)}
        <div
          class="loc"
          class:vide={z.points === 0}
          style="flex-grow: {z.part}; --part: {Math.round((z.part / 30) * 100)}%"
        >
          <span class="pp">{z.points}</span>
          <span class="lib">{z.nom}</span>
          <span class="plage">{z.min}–{z.max}</span>
          {#if z.piece}
            <span class="couvreur">
              {z.piece}{#if z.usure}&nbsp;<span class="usure">{z.usure}</span>{/if}
            </span>
          {/if}
        </div>
      {/each}
    </div>
    <p class="moyenne">
      Protection moyenne pondérée
      <span class="data">{moyenne.toFixed(1).replace('.', ',')}</span>
    </p>
    {#if nues.length > 0}
      <p class="alerte">
        Sans Protection : {nues.map((z) => z.nom).join(', ')} — {partNue} % des
        coups y tombent sans rien rencontrer.
      </p>
    {/if}
  </div>
</div>

{#if abimes.length > 0}
  <div class="cartouche">
    <div class="bandeau">À réparer</div>
    <div class="corps">
      <p class="regle">
        Réparer demande un <strong>Test d'Artisanat Complexe (−10)</strong> et
        consomme 1 Matériel de réparation — que le Test réussisse ou non. Une
        Réussite remet la pièce en état d'un seul coup.
        <button type="button" class="lien" onclick={() => ouvrirJet('artisanat')}>
          Jeter l'Artisanat
        </button>
      </p>
      <ul class="plates">
        {#each abimes as a (a.id)}
          <li class="abime">
            <span class="quoi">
              <span class="nom">{a.nom} <span class="usure">{a.usure}</span></span>
              <span class="detail">{a.resume}</span>
            </span>
            <span class="gestes">
              <button
                type="button"
                class="pilule"
                disabled={materiel === 0}
                onclick={() => reparer(a.id, true)}
              >
                Réparée
              </button>
              <button
                type="button"
                class="pilule discret"
                disabled={materiel === 0}
                onclick={() => reparer(a.id, false)}
              >
                Tentative ratée
              </button>
            </span>
          </li>
        {/each}
      </ul>
      <p class="materiel" class:sec={materiel === 0}>
        {materiel} Matériel{materiel > 1 ? 's' : ''} de réparation
        {#if materiel === 0}— il en faut au moins un pour essayer.{/if}
      </p>
    </div>
  </div>
{/if}

<!-- La nature de l'objet, d'un coup d'œil. Quatre dessins valent mieux que
     quatre mots répétés quarante fois : l'épée, la cuirasse, la caisse, et la
     plume pour ce que la table a inventé. -->
{#snippet icone(genre: string)}
  {#if genre === 'arme'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"
      ><path d="M4 20 18 6" /><path d="M14 4l6 6" /><path d="M8 14l-3 3 2 2 3-3" /></svg
    >
  {:else if genre === 'armure'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"
      ><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /></svg
    >
  {:else if genre === 'libre'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"
      ><path d="M4 20h4l10-10-4-4L4 16z" /><path d="M13 7l4 4" /></svg
    >
  {:else}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"
      ><rect x="4" y="8" width="16" height="12" rx="2" /><path d="M9 8V5h6v3" /><path
        d="M4 13h16"
      /></svg
    >
  {/if}
{/snippet}

<!-- Les trois états, dessinés : soi, le sac, la maison. Le libellé complet
     reste sur le bouton — au survol, et pour qui lit la page autrement. -->
{#snippet etatIcone(id: Rangement)}
  {#if id === 'equipe'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"
      ><circle cx="12" cy="7" r="4" /><path d="M4 21v-2a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v2" /></svg
    >
  {:else if id === 'sac'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"
      ><rect x="5" y="7" width="14" height="14" rx="3" /><path d="M9 7V5a3 3 0 0 1 6 0v2" /><path
        d="M8 13h8"
      /></svg
    >
  {:else}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"
      ><path d="M3 11l9-8 9 8" /><path d="M5 10v10h14V10" /><path d="M10 20v-6h4v6" /></svg
    >
  {/if}
{/snippet}

<div class="cartouche">
  <div class="bandeau">Inventaire</div>
  <div class="corps">
    {#if contenu.lignes.length === 0}
      <p class="vide">Rien pour l'instant.</p>
    {:else}
      <p class="regle">
        Glissez une pièce d'un groupe à l'autre, ou servez-vous des trois
        boutons : ils font la même chose, et eux marchent partout.
      </p>
      {#each parEtat as groupe (groupe.id)}
        <section
          class="etat etat-{groupe.id}"
          class:cible={glisse !== null && glisse.depuis !== groupe.id}
          aria-label={groupe.titre}
          ondragover={(e) => survoler(e, groupe.id)}
          ondrop={(e) => deposer(e, groupe.id)}
        >
          <h3>
            {groupe.titre}
            <span class="compte data">{groupe.lignes.length}</span>
            {#if groupe.id === 'equipe' && armesSurSoi > 0}
              <span class="emplacements" class:trop={armesSurSoi > 3}>
                {armesSurSoi} / 3 emplacements d'arme
              </span>
            {/if}
          </h3>

          {#if groupe.id === 'equipe' && armesSurSoi > 3}
            <p class="alerte">
              Le livre n'accorde que trois emplacements d'arme, dont une seule
              arme active à la fois. Au-delà, c'est au meneur de trancher.
            </p>
          {/if}

          {#if groupe.lignes.length === 0}
            <p class="vide">{groupe.vide}</p>
          {/if}

          <div class="pieces">
            {#each groupe.lignes as o (o.id + o.source)}
              <!-- Les Dégâts montent en vedette et quittent la liste des
                   traits : un même nombre deux fois dirait deux choses. -->
              {@const formule = o.traits.find((t) => t.intitule === 'Dégâts')}
              {@const autres =
                o.degats === null ? o.traits : o.traits.filter((t) => t.intitule !== 'Dégâts')}
              <article
                class="piece"
                class:tiree={glisse !== null && glisse.id === o.id}
                class:laissee={!o.emporte}
                draggable="true"
                ondragstart={(e) => prendre(e, o)}
                ondragend={() => (glisse = null)}
              >
                <header class="tete">
                  <span class="marque">{@render icone(o.genre)}</span>
                  <!-- Ce que la table a écrit se corrige en place ; ce qui
                       vient du livre garde le nom du livre. -->
                  {#if o.genre === 'libre'}
                    <input
                      class="saisie libelle"
                      type="text"
                      draggable="false"
                      value={o.nom}
                      aria-label="Nom de l'objet"
                      onchange={(e) => renommerObjet(o.id, e.currentTarget.value)}
                    />
                  {:else}
                    <span class="nom">{o.nom}</span>
                  {/if}
                  {#if o.quantite > 1}<span class="niveau data">×{o.quantite}</span>{/if}
                  {#if o.usure}<span class="usure">{o.usure}</span>{/if}
                  {#if o.renfort !== 0}
                    <span class="niveau data">
                      {o.renfort < 0 ? '−' : '+'}{Math.abs(o.renfort)}
                    </span>
                  {/if}
                  {#if o.encombrement > 0}
                    <span class="poids data">{o.encombrement} enc.</span>
                  {/if}
                </header>

                <span class="detail">
                  {GENRES[o.genre] ?? o.genre}
                  {#if o.charges_restantes !== null}· {o.charges_restantes} usages{/if}
                </span>

                <!-- Ce qu'une arme frappe vraiment. Le livre écrit « 6 + FF » ;
                     au sac, ce n'est pas un chiffre, c'est une addition à faire
                     de tête à chaque coup. La fiche la fait. -->
                {#if o.degats !== null}
                  <div class="vedette">
                    <span class="chiffre data">{o.degats}</span>
                    <span class="titre">
                      Dégâts
                      {#if formule && formule.valeur.includes('FF')}
                        <span class="formule">{formule.valeur} · Force de frappe comprise</span>
                      {/if}
                    </span>
                  </div>
                {/if}

                <!-- Ce que le catalogue dit de l'objet. C'est ce qui manquait le
                     plus : l'inventaire nommait les armes sans jamais dire ce
                     qu'elles font, et il fallait rouvrir le livre. -->
                {#if o.genre !== 'libre' && autres.length > 0}
                  <dl class="traits">
                    {#each autres as t (t.intitule)}
                      <div class="trait" class:modifie={t.modifie}>
                        <dt>{t.intitule}</dt>
                        <dd class="data">{t.valeur}</dd>
                      </div>
                    {/each}
                  </dl>
                {/if}

                {#if o.genre === 'libre'}
                  <input
                    class="saisie note-saisie"
                    type="text"
                    draggable="false"
                    value={o.note ?? ''}
                    placeholder="Ce que le meneur en a dit"
                    aria-label="Note sur l'objet"
                    onchange={(e) => annoterObjet(o.id, e.currentTarget.value)}
                  />
                  <!-- Un objet que la table a inventé peut peser sur les jets —
                       mais seulement porté, comme les Propriétés d'une armure. -->
                  <Chiffres
                    poses={o.traits.map((t, position) => ({
                      position,
                      dit: t.intitule,
                      valeur: Number(t.valeur.replace('−', '-')),
                    }))}
                    ajouter={(cible, id, valeur) => chiffrerObjet(o.id, cible, id, valeur)}
                    corriger={(position, valeur) => corrigerChiffreObjet(o.id, position, valeur)}
                    effacer={(position) => effacerChiffreObjet(o.id, position)}
                  />
                {:else if o.note}
                  <span class="detail note">{o.note}</span>
                {/if}

                {#each o.renforts as r (r.source + r.sens)}
                  <span class="detail rituel">{r.dit}</span>
                {/each}
                {#each o.ameliorations as a (a.id)}
                  <span class="detail rituel">{a.nom} · {a.dit}</span>
                {/each}

                <footer class="controles">
                  <!-- Trois boutons plutôt qu'une bascule : avec trois états,
                       une bascule oblige à traverser celui dont on ne veut pas. -->
                  <span class="rangement" role="group" aria-label="Où ranger {o.nom}">
                    {#each ETATS as e (e.id)}
                      <button
                        type="button"
                        class="case"
                        aria-pressed={o.rangement === e.id}
                        aria-label={e.titre}
                        title={e.titre}
                        onclick={() => rangerObjet(o.id, e.id)}
                      >
                        {@render etatIcone(e.id)}
                      </button>
                    {/each}
                  </span>

                  <!-- Le moins jette un exemplaire ; sur le dernier, la ligne
                       s'en va. C'est le même geste que l'ancien « Jeter ». -->
                  <span class="pile">
                    <button
                      type="button"
                      class="cran"
                      aria-label="Jeter un {o.nom}"
                      onclick={() => seDefaire(o.id, false)}
                    >
                      −
                    </button>
                    <span class="data" aria-label="{o.quantite} en tout">{o.quantite}</span>
                    <button
                      type="button"
                      class="cran"
                      aria-label="Un {o.nom} de plus"
                      onclick={() => unDePlus(o)}
                    >
                      +
                    </button>
                  </span>

                  {#if o.revente !== null}
                    <button type="button" class="pilule" onclick={() => seDefaire(o.id, true)}>
                      Vendre <span class="data">{o.revente} £</span>
                    </button>
                  {/if}
                </footer>
              </article>
            {/each}
          </div>
        </section>
      {/each}
    {/if}
  </div>
</div>

<Renforcement {personnage} {renforcer} {ameliorer} {attribuer} {monter} />

<div class="cartouche">
  <div class="bandeau">Ramasser · acheter</div>
  <div class="corps">
    <input
      class="saisie"
      type="search"
      placeholder="Chercher dans le commerce — couteau, cuir, corde…"
      bind:value={recherche}
      aria-label="Chercher un objet"
    />
    {#if dejaEcrits.length > 0}
      <div class="deja">
        <span class="detail">Déjà écrit :</span>
        {#each dejaEcrits as nom (nom)}
          <button type="button" class="pilule" onclick={() => reprendre(nom)}>+ {nom}</button>
        {/each}
      </div>
    {/if}
    {#if trouves.length > 0}
      <ul class="plates">
        {#each trouves as a (a.id)}
          <li class="ligne">
            <span class="quoi">
              <span class="nom">{a.nom}</span>
              <span class="detail">
                {GENRES[a.genre] ?? a.genre}
                {#if a.encombrement > 0}· {a.encombrement} enc.{/if}
                {#if a.prix !== null}· {a.prix} £{/if}
              </span>
            </span>
            <span class="gestes">
              <button type="button" class="pilule" onclick={() => ramasser(a.id, false)}>
                Ramasser
              </button>
              {#if a.prix !== null}
                <button
                  type="button"
                  class="pilule"
                  disabled={a.prix > enBourse}
                  onclick={() => ramasser(a.id, true)}
                >
                  Acheter <span class="data">{a.prix} £</span>
                </button>
              {/if}
            </span>
          </li>
        {/each}
      </ul>
    {:else if recherche.trim().length >= 2}
      <p class="vide">Rien de ce nom dans les catalogues.</p>
    {:else}
      <p class="regle">
        Le butin se ramasse sans rien payer ; l'étal, lui, se règle. Un objet
        revendu ne rend que la moitié de son prix, et le Marchandage fait bouger
        le reste.
      </p>
    {/if}

    <!-- Ce qu'aucun catalogue ne porte s'inscrit quand même : le livre ne
         prétend pas tout lister, et une table invente. -->
    {#if ouvert}
      <div class="invention">
        <label class="champ">
          Nom
          <input
            class="saisie"
            type="text"
            placeholder="Lettre scellée, fiole sans étiquette…"
            bind:value={nom}
          />
        </label>
        <label class="champ">
          Encombrement
          <input class="saisie court" type="number" min="0" max="20" bind:value={poids} />
        </label>
        <label class="champ">
          Prix
          <input
            class="saisie court"
            type="number"
            min="0"
            placeholder="—"
            bind:value={valeur}
          />
        </label>
        <label class="champ large">
          Note
          <input
            class="saisie"
            type="text"
            placeholder="Ce que le meneur en a dit"
            bind:value={note}
          />
        </label>
        <div class="gestes">
          <button
            type="button"
            class="pilule"
            disabled={nom.trim().length === 0}
            onclick={inscrireEtRefermer}
          >
            Inscrire au sac
          </button>
          <button type="button" class="lien" onclick={() => (ouvert = false)}>Annuler</button>
        </div>
        <p class="regle">
          Un objet écrit à la main pèse comme les autres et se revend à la
          moitié du prix que vous lui donnez. Sans prix, il ne se vend pas.
        </p>
      </div>
    {:else}
      <button type="button" class="lien" onclick={() => (ouvert = true)}>
        Rien de tout ça ? Écrire l'objet à la main
      </button>
    {/if}
  </div>
</div>

{#if vue.a_arbitrer.length > 0}
  <div class="cartouche">
    <div class="bandeau">À trancher à la table</div>
    <div class="corps">
      <ul class="plates">
        {#each vue.a_arbitrer as ligne, i (ligne + i)}
          <li>{ligne}</li>
        {/each}
      </ul>
    </div>
  </div>
{/if}

<style>
  /* Ce qu'on a déjà écrit, à portée d'un geste. */
  .deja {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--pas);
    margin-top: var(--pas);
  }
  /* La bande elle-même vient du système de design (`.localisations`). Ne
     reste ici que ce qui est propre au Sac : l'alarme sur une zone nue, et
     la pièce qui couvre. */

  /* Une zone que rien ne couvre doit alerter, pas s'effacer : c'est elle
     qu'on veut traiter. On garde le trait rompu du système, on lui rend sa
     lisibilité, et le chiffre passe au rouge. */
  .localisations .loc.vide {
    opacity: 1;
    border-color: var(--ko);
  }
  .localisations .loc.vide .pp {
    color: var(--ko);
  }
  .couvreur {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.68rem;
    line-height: 1.3;
    color: var(--gold);
  }

  /* Sous les 10 % de la Tête, aucune étiquette ne tient dans une bande
     horizontale : à 375 px, sa cellule fait 33 px. On tourne alors la table
     d'un quart de tour — une ligne par Localisation — et la part des coups
     devient le remplissage de la ligne, le Torse allant jusqu'au bout. */
  /* Le pivotement de la bande vient du système ; ici on ne fait que ranger
     ce que la cellule du Sac contient, une fois la ligne devenue large. */
  @media (max-width: 40rem) {
    .localisations .loc {
      display: grid;
      grid-template-columns: auto 1fr auto;
      align-items: baseline;
      gap: 0 calc(var(--pas) * 0.75);
    }
    .localisations .lib {
      order: 1;
    }
    .localisations .plage {
      order: 2;
    }
    .localisations .pp {
      order: 3;
      font-size: 1rem;
    }
    .localisations .couvreur {
      order: 4;
      grid-column: 1 / -1;
    }
  }
  .moyenne {
    margin: 0;
    font-size: 0.85rem;
    color: var(--ink-soft);
  }

  /* L'inventaire par état. Le titre porte le compte, pour qu'on sache d'un
     coup d'œil ce qu'on emporte sans lire la liste. */
  .etat {
    margin-bottom: calc(var(--pas) * 3);
    border-radius: var(--r);
    transition: box-shadow 0.12s ease-out;
  }
  /* Le groupe qui accepterait la pièce qu'on traîne. */
  .etat.cible {
    box-shadow: 0 0 0 2px var(--gold);
  }
  .emplacements {
    margin-left: auto;
    font-family: system-ui, sans-serif;
    font-size: 0.7rem;
    letter-spacing: 0;
    text-transform: none;
    color: var(--ink-soft);
  }
  .emplacements.trop {
    color: var(--ko);
  }

  /* Les pièces en cartes plutôt qu'en lignes : chacune tient ses traits et
     ses gestes, et la grille se replie toute seule sur un téléphone. */
  .pieces {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 15rem), 1fr));
    gap: var(--pas);
  }
  .piece {
    display: flex;
    flex-direction: column;
    gap: calc(var(--pas) * 0.5);
    padding: var(--pas);
    border: 1px solid var(--line);
    border-radius: var(--r);
    background: var(--surface-2);
    cursor: grab;
  }
  .piece.tiree {
    opacity: 0.45;
  }
  .tete {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: calc(var(--pas) * 0.5);
  }
  .tete .nom,
  .tete .libelle {
    flex: 1 1 8rem;
    min-width: 0;
  }
  /* Le dessin de la nature, et le poids au bout de la même ligne. */
  .marque {
    display: inline-flex;
    align-self: center;
    flex: none;
    color: var(--ink-soft);
  }
  .marque svg {
    width: 1.15rem;
    height: 1.15rem;
  }
  .poids {
    margin-left: auto;
    font-size: 0.75rem;
    color: var(--gold);
    white-space: nowrap;
  }
  .note-saisie {
    font-size: 0.8rem;
  }

  /* Les Dégâts en vedette : le seul nombre qu'on cherche une arme à la main. */
  .vedette {
    display: flex;
    align-items: center;
    gap: calc(var(--pas) * 0.75);
    padding: calc(var(--pas) * 0.5) calc(var(--pas) * 0.75);
    border: 1px solid var(--line-fort);
    border-radius: var(--r);
    background: var(--surface-2);
  }
  .vedette .chiffre {
    font-size: 1.6rem;
    font-weight: 600;
    line-height: 1;
    color: var(--gold);
  }
  .vedette .titre {
    display: flex;
    flex-direction: column;
    gap: 1px;
    font-family: 'Cinzel', serif;
    font-size: 0.68rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink-soft);
  }
  .vedette .formule {
    font-family: 'EB Garamond', Georgia, serif;
    font-size: 0.7rem;
    letter-spacing: 0;
    text-transform: none;
  }
  .controles {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: calc(var(--pas) * 0.75);
    margin-top: auto;
    padding-top: calc(var(--pas) * 0.75);
    border-top: 1px solid var(--line);
  }
  /* Les trois états deviennent trois dessins : soi, le sac, la maison. Le
     libellé complet reste au survol et pour les lecteurs d'écran. */
  .rangement .case {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: calc(var(--pas) * 0.6) calc(var(--pas) * 0.9);
  }
  .rangement .case svg {
    width: 1.05rem;
    height: 1.05rem;
  }

  /* Combien on en a : le moins jette, le plus en reprend un. */
  .pile {
    display: inline-flex;
    align-items: center;
    gap: calc(var(--pas) * 0.5);
    border: 1px solid var(--line);
    border-radius: var(--r);
    padding: 0 calc(var(--pas) * 0.5);
  }
  .pile .cran {
    border: none;
    background: transparent;
    color: var(--ink);
    font-size: 1rem;
    line-height: 1;
    min-width: 1.75rem;
    min-height: 1.75rem;
    cursor: pointer;
  }
  .pile .cran:hover {
    color: var(--gold);
  }

  /* Même règle qu'ailleurs : au doigt, les cibles montent à 44 px. */
  @media (pointer: coarse) {
    .pile .cran {
      min-width: 2.75rem;
      min-height: 2.75rem;
    }
    .rangement .case {
      padding: calc(var(--pas) * 1.1) calc(var(--pas) * 1.4);
    }
  }
  .etat:last-child {
    margin-bottom: 0;
  }
  /* Les trois groupes se refroidissent de haut en bas : ce qu'on a sur le
     dos porte le rouge des bandeaux, le sac reste neutre, et ce qu'on laisse
     s'enfonce. La température fait le travail que trois titres gris ne
     faisaient pas. */
  .etat h3 {
    font-family: 'Cinzel', serif;
    font-weight: normal;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-soft);
    margin: 0 0 var(--pas);
    padding: calc(var(--pas) * 0.75) var(--pas);
    border-radius: var(--r);
    display: flex;
    align-items: baseline;
    gap: var(--pas);
  }
  .etat-equipe h3 {
    background: var(--accent);
    color: var(--accent-txt);
  }
  .etat-equipe h3 .compte,
  .etat-equipe h3 .emplacements {
    color: var(--accent-txt);
  }
  .etat-sac h3 {
    background: var(--surface-2);
    color: var(--ink);
    border-left: 3px solid var(--line-fort);
  }
  .etat-laisse h3 {
    border-bottom: 1px dashed var(--line);
    border-radius: 0;
    padding-left: 0;
  }
  /* Ce qui dort à la maison s'enfonce dans la page : hors du voyage. */
  .etat-laisse {
    background: var(--bg);
    padding: var(--pas);
    margin-left: calc(var(--pas) * -1);
    margin-right: calc(var(--pas) * -1);
  }
  .etat h3 .compte {
    font-size: 0.75rem;
  }
  /* Ce qu'on a laissé reste lisible mais s'efface : il n'est pas du voyage. */
  .piece.laissee {
    opacity: 0.6;
  }

  /* Les traits du catalogue : Dégâts, Protection, Couvre... */
  .traits {
    display: flex;
    flex-wrap: wrap;
    gap: calc(var(--pas) * 0.5) calc(var(--pas) * 2);
    margin: calc(var(--pas) * 0.5) 0 0;
    grid-column: 1 / -1;
  }
  .trait {
    display: flex;
    align-items: baseline;
    gap: calc(var(--pas) * 0.5);
    font-size: 0.8rem;
  }
  .trait dt {
    color: var(--ink-soft);
    /* « Couvre » et ses deux points ne se séparent pas : dans une carte
       étroite, la valeur passe à la ligne, l'intitulé jamais. */
    white-space: nowrap;
  }
  .trait dd {
    min-width: 0;
  }
  .trait dt::after {
    content: ' :';
  }
  .trait dd {
    margin: 0;
  }
  /* Modifié par un Renforcement : le chiffre n'est plus celui du livre. */
  .trait.modifie dd {
    border-bottom: 1px dotted var(--line-fort);
  }

  /* Les trois états, en un seul groupe de boutons. */
  .rangement {
    display: inline-flex;
    border: 1px solid var(--line);
    border-radius: var(--r);
    overflow: hidden;
  }
  .rangement .case {
    border: none;
    background: var(--surface-2);
    color: var(--ink-soft);
    font-size: 0.7rem;
    padding: calc(var(--pas) * 0.75) calc(var(--pas) * 1.5);
    cursor: pointer;
    border-right: 1px solid var(--line);
  }
  .rangement .case:last-child {
    border-right: none;
  }
  .rangement .case[aria-pressed='true'] {
    background: var(--surface-1);
    color: var(--ink);
    font-weight: 600;
  }
  /* Donner ou retirer : trois éléments sur une ligne, sous le montant. */
  .mouvement {
    display: flex;
    align-items: center;
    gap: calc(var(--pas) / 2);
    margin-top: var(--pas);
  }
  .mouvement .saisie.court {
    width: 8ch;
  }
  /* Ce qu'un Rituel a fait à l'exemplaire : à côté de l'usure, même famille. */
  .rituel {
    color: var(--gold);
  }
  /* Ce que le meneur en a dit : en retrait, pour ne pas se confondre avec ce
     que le catalogue affirme. */
  .note {
    font-style: italic;
  }
  /* Le formulaire de l'objet inventé : quatre champs sur une grille, pour
     qu'il ne prenne pas plus de place qu'une ligne d'inventaire. */
  .invention {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: var(--pas);
    align-items: end;
    margin-top: var(--pas);
    padding-top: var(--pas);
    border-top: 1px solid var(--trait);
  }
  .invention .champ {
    display: grid;
    gap: 2px;
    font-size: 0.68rem;
    font-family: var(--titre);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--pale);
  }
  .invention .champ.large,
  .invention .gestes,
  .invention .regle {
    grid-column: 1 / -1;
  }
  .invention .saisie.court {
    width: 8ch;
  }
  .entete {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    gap: calc(var(--pas) * 3);
  }
  .cartouche {
    margin-bottom: calc(var(--pas) * 3);
  }
  .cartouche .corps {
    padding: calc(var(--pas) * 3);
  }
  .argent,
  .charge {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 600;
    color: var(--gold);
  }
  .charge.trop {
    color: var(--ko);
  }
  .alerte {
    margin: var(--pas) 0 0;
    font-size: 0.78rem;
    color: var(--ko);
  }
  .vide {
    margin: 0;
    color: var(--ink-soft);
    font-size: 0.9rem;
  }
  .plates {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 0;
  }
  .plates li {
    display: flex;
    align-items: baseline;
    gap: calc(var(--pas) * 2);
    font-size: 0.9rem;
    padding: calc(var(--pas) * 1.5) 0;
    border-bottom: 1px solid var(--line);
  }
  .plates li:last-child {
    border-bottom: none;
  }
  .ligne,
  .abime {
    justify-content: space-between;
    flex-wrap: wrap;
    gap: calc(var(--pas) * 2);
  }
  .quoi {
    display: grid;
    gap: 2px;
  }
  .nom {
    display: flex;
    align-items: baseline;
    gap: var(--pas);
  }
  .niveau {
    font-size: 0.75rem;
    color: var(--ink-soft);
  }
  .usure {
    font-family: 'Cinzel', serif;
    font-size: 0.6rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ko);
  }
  .detail {
    font-size: 0.75rem;
    color: var(--ink-soft);
  }
  .gestes {
    display: flex;
    gap: var(--pas);
    flex-wrap: wrap;
  }
  .discret {
    opacity: 0.7;
    font-size: 0.6rem;
  }

  .regle {
    margin: 0 0 calc(var(--pas) * 3);
    font-size: 0.82rem;
    color: var(--ink-soft);
  }
  .materiel {
    margin: calc(var(--pas) * 3) 0 0;
    font-size: 0.8rem;
    color: var(--ink-soft);
  }
  .materiel.sec {
    color: var(--ko);
  }
  .lien {
    background: none;
    border: none;
    color: var(--gold);
    text-decoration: underline;
    cursor: pointer;
    font: inherit;
    font-size: 0.82rem;
    padding: 0;
  }
  .saisie {
    width: 100%;
    margin-bottom: calc(var(--pas) * 3);
  }
</style>
